import { z } from 'zod';
import { FULFILLMENT_TYPES, ORDER_STATUSES } from './status.js';
import { EMPLOYEE_ROLES } from './roles.js';
import { itemConfigSchema } from './menu.js';

export const loginSchema = z.object({
  email: z.string().email().max(200),
  password: z.string().min(1).max(200),
});

export const registerSchema = z.object({
  email: z.string().email().max(200),
  password: z.string().min(8).max(200),
  displayName: z.string().min(1).max(100),
  restaurantSlug: z.string().max(80).optional(),
});

export const addressSchema = z.object({
  line1: z.string().min(3).max(200),
  line2: z.string().max(100).optional().default(''),
  city: z.string().min(1).max(100),
  postalCode: z.string().max(20).optional().default(''),
  instructions: z.string().max(300).optional().default(''),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
});
export type DeliveryAddress = z.infer<typeof addressSchema>;

export const cartLineSchema = z.object({
  menuItemId: z.string().uuid(),
  quantity: z.number().int().min(1).max(50),
  selections: z.record(z.array(z.string().max(40)).max(30)).default({}),
  note: z.string().max(200).optional(),
});
export type CartLineInput = z.infer<typeof cartLineSchema>;

export const createOrderSchema = z.object({
  fulfillmentType: z.enum(FULFILLMENT_TYPES),
  items: z.array(cartLineSchema).min(1).max(60),
  customer: z.object({
    name: z.string().min(1).max(100),
    phone: z.string().min(7).max(30),
    email: z.string().email().max(200),
  }),
  address: addressSchema.optional(),
  instructions: z.string().max(500).optional().default(''),
  tipPercent: z.number().int().min(0).max(50).optional(),
  tipCents: z.number().int().min(0).max(100_000).optional(),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;

export const payOrderSchema = z.object({
  card: z.object({
    number: z.string().min(12).max(23),
    expiry: z.string().regex(/^(0[1-9]|1[0-2])\/\d{2}$/),
    cvc: z.string().regex(/^\d{3,4}$/),
    name: z.string().max(100).optional(),
  }),
});

export const statusUpdateSchema = z.object({
  status: z.enum(ORDER_STATUSES),
  reason: z.string().max(300).optional(),
});

export const scanQrSchema = z.object({ token: z.string().min(16).max(200) });

export const locationSchema = z.object({
  orderId: z.string().uuid(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyM: z.number().min(0).max(100_000).nullish(),
  headingDeg: z.number().min(0).max(360).nullish(),
  speedMps: z.number().min(0).max(200).nullish(),
  capturedAt: z.string().datetime().optional(),
  /** Set by the driver app's demo simulator; stored as SIMULATOR instead of GPS. */
  simulated: z.boolean().optional(),
});
export type LocationInput = z.infer<typeof locationSchema>;

export const driverStatusSchema = z.object({ status: z.enum(['AVAILABLE', 'OFFLINE']) });

export const categoryInputSchema = z.object({
  name: z.string().min(1).max(80),
  sortOrder: z.number().int().min(0).max(1000).optional(),
  active: z.boolean().optional(),
});

export const menuItemInputSchema = z.object({
  categoryId: z.string().uuid().nullable().optional(),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional().default(''),
  basePriceCents: z.number().int().min(0).max(100_000),
  imageUrl: z.string().max(500).nullable().optional(),
  active: z.boolean().optional(),
  config: itemConfigSchema.optional(),
});

export const employeeCreateSchema = z.object({
  email: z.string().email().max(200),
  displayName: z.string().min(1).max(100),
  role: z.enum(EMPLOYEE_ROLES),
  password: z.string().min(8).max(200),
});

export const employeeUpdateSchema = z.object({
  role: z.enum(EMPLOYEE_ROLES).optional(),
  status: z.enum(['ACTIVE', 'DISABLED']).optional(),
});

export const restaurantStatusSchema = z.object({ status: z.enum(['ACTIVE', 'SUSPENDED']) });

export const assignDriverSchema = z.object({ driverUserId: z.string().uuid().nullable() });

export const simulatorStartSchema = z.object({ orderId: z.string().uuid(), intervalMs: z.number().int().min(500).max(5000).optional() });

/** Realtime payloads */
export interface LocationPayload {
  orderId: string;
  lat: number;
  lng: number;
  accuracyM: number | null;
  headingDeg: number | null;
  speedMps: number | null;
  capturedAt: string;
  etaMinutes?: number;
}

export const REALTIME_EVENTS = [
  'order.created',
  'order.status.changed',
  'order.ready',
  'order.driver.assigned',
  'order.picked_up',
  'order.out_for_delivery',
  'order.delivered',
  'driver.location.updated',
  'driver.availability.changed',
] as const;
export type RealtimeEvent = (typeof REALTIME_EVENTS)[number];

export const rooms = {
  restaurantOrders: (restaurantId: string) => `restaurant:${restaurantId}:orders`,
  restaurantDispatch: (restaurantId: string) => `restaurant:${restaurantId}:dispatch`,
  order: (orderId: string) => `order:${orderId}`,
  driver: (driverId: string) => `driver:${driverId}`,
};
